import React, { useEffect, useRef } from 'react';

import { useIntl } from 'react-intl-next';
import { graphql, useFragment } from 'react-relay';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Text, xcss } from '@atlaskit/primitives';
import UFOSegment from '@atlaskit/react-ufo/segment';

import { ACTION_SUBJECT_PREFIX } from '../../../../common/constants/analytics';
import { ApplicationModes } from '../../../../common/constants/application-modes';
import { useAppContext } from '../../../../common/ui/app-context';
import { Link } from '../../../../common/ui/link-component';
import { areLLMPoweredFeaturesEnabled } from '../../../../common/utils/entitlement-checks';
import { sha256Hash } from '../../../../common/utils/events/utils';
import { isHello } from '../../../../common/utils/is-hello';
import { useAnalytics, useCommonAndPrivateAttributes } from '../../../../controllers/analytics';

import type { autoCorrection_AtlassianSearchPage$key } from './__generated__/autoCorrection_AtlassianSearchPage.graphql';

const anchorStyles = xcss({
	color: 'color.link',
	textDecoration: 'none',

	':hover': {
		textDecoration: 'underline',
	},
	':active': {
		textDecoration: 'none',
	},
	':visited': {
		color: 'color.link.visited',
	},
	':visited:active': {
		color: 'color.link.visited.pressed',
	},
});

const useFireClickEvent = (cloudId: string | undefined) => {
	const { fireAnalyticsEvent } = useAnalytics();
	const { commonAttributes } = useCommonAndPrivateAttributes();

	return (additionalAttributes?: Record<string, any>) => {
		// Check if this is a hello tenant - only hello tenants can pass sensitive fields
		const isHelloTenant: boolean = isHello(cloudId);

		let filteredAttributes = { ...additionalAttributes };
		if (!isHelloTenant) {
			// For non-hello tenants, replace fields with values to [REDACTED], keep null as null
			filteredAttributes = {
				...filteredAttributes,
				originalQuery: additionalAttributes?.originalQuery ? '[REDACTED]' : null,
				replacementQuery: additionalAttributes?.replacementQuery ? '[REDACTED]' : null,
				suggestedQuery: additionalAttributes?.suggestedQuery ? '[REDACTED]' : null,
			};
		}

		fireAnalyticsEvent({
			eventType: 'ui',
			actionSubject: `${ACTION_SUBJECT_PREFIX}AutoCorrection`,
			action: 'clicked',
			attributes: {
				commonAttributes,
				additionalAttributes: filteredAttributes,
			},
		});
	};
};

const useFireShownEvent = (cloudId: string | undefined) => {
	const { fireAnalyticsEvent } = useAnalytics();
	const { commonAttributes } = useCommonAndPrivateAttributes();

	return (additionalAttributes?: Record<string, any>) => {
		// Check if this is a hello tenant - only hello tenants can pass sensitive fields
		const isHelloTenant: boolean = isHello(cloudId);

		let filteredAttributes = { ...additionalAttributes };
		if (!isHelloTenant) {
			// For non-hello tenants, replace fields with values to [REDACTED], keep null as null
			filteredAttributes = {
				...filteredAttributes,
				originalQuery: additionalAttributes?.originalQuery ? '[REDACTED]' : null,
				replacementQuery: additionalAttributes?.replacementQuery ? '[REDACTED]' : null,
				suggestedQuery: additionalAttributes?.suggestedQuery ? '[REDACTED]' : null,
			};
		}

		fireAnalyticsEvent({
			eventType: 'ui',
			actionSubject: `${ACTION_SUBJECT_PREFIX}AutoCorrection`,
			action: 'shown',
			attributes: {
				commonAttributes,
				additionalAttributes: filteredAttributes,
			},
		});
	};
};

const isEnglishLocale = (locale: string): boolean => {
	const lowerCaseLocale = locale.toLowerCase();
	// English locale or starts with 'en-', `en-US`, `en-GB`, etc.
	return lowerCaseLocale === 'en' || lowerCaseLocale.startsWith('en-');
};

export type AutoCorrectionProps = {
	node: autoCorrection_AtlassianSearchPage$key;
	cloudId: string | undefined;
};

const AutoCorrection = ({ node, cloudId }: AutoCorrectionProps) => {
	const queryInfo = useFragment(
		graphql`
			fragment autoCorrection_AtlassianSearchPage on SearchQueryInfo {
				originalQuery
				replacementQuery
				suggestedQuery
				originalQueryType
				confidenceScore
				shouldTriggerAutocorrectionExperience
			}
		`,
		node,
	);
	const { generateSearchUrl, queryParams } = useAppContext();
	const fireClickEvent = useFireClickEvent(cloudId);
	const fireShownEvent = useFireShownEvent(cloudId);
	const hasTrackedShown = useRef<string | false>(false);

	const originalQueryUrlParam = queryParams.originalQuery;

	const {
		originalQuery,
		replacementQuery,
		suggestedQuery,
		originalQueryType,
		confidenceScore,
		shouldTriggerAutocorrectionExperience,
	} = queryInfo || {};

	/**
	 * Show auto correction if:
	 * - replacementQuery is available
	 * - shouldTriggerAutocorrectionExperience is true (currently not available)
	 */
	const showAutoCorrection =
		(replacementQuery || suggestedQuery) && shouldTriggerAutocorrectionExperience;

	// Fire shown events when auto-correction is displayed
	useEffect(() => {
		const {
			originalQuery,
			replacementQuery,
			suggestedQuery,
			originalQueryType,
			confidenceScore,
			shouldTriggerAutocorrectionExperience,
		} = queryInfo || {};

		const showAutoCorrection =
			(replacementQuery || suggestedQuery) && shouldTriggerAutocorrectionExperience;

		if (showAutoCorrection) {
			// Generate a unique key for the current auto-correction state
			// Only depend on the actual auto-correction data, not URL parameters
			const autoCorrectionKey = `${replacementQuery || ''}-${suggestedQuery || ''}`;

			if (hasTrackedShown.current !== autoCorrectionKey) {
				hasTrackedShown.current = autoCorrectionKey;

				// Fire shown events based on what's actually displayed
				if (!originalQueryUrlParam && replacementQuery) {
					// Fire shown events for replacement and original
					fireShownEvent({
						autoCorrectionType: 'replacement',
						originalQueryType,
						confidenceScore,
						originalQuery,
						replacementQuery,
						suggestedQuery: null,
						originalQueryHash: originalQuery ? sha256Hash(originalQuery) : null,
						replacementQueryHash: replacementQuery ? sha256Hash(replacementQuery) : null,
						suggestedQueryHash: null,
					});
					fireShownEvent({
						autoCorrectionType: 'original',
						originalQueryType,
						confidenceScore,
						originalQuery,
						replacementQuery,
						suggestedQuery: null,
						originalQueryHash: originalQuery ? sha256Hash(originalQuery) : null,
						replacementQueryHash: replacementQuery ? sha256Hash(replacementQuery) : null,
						suggestedQueryHash: null,
					});
				}

				if (!replacementQuery && suggestedQuery) {
					// Fire shown event for suggested
					fireShownEvent({
						autoCorrectionType: 'suggested',
						originalQueryType,
						confidenceScore,
						originalQuery,
						replacementQuery: null,
						suggestedQuery,
						originalQueryHash: originalQuery ? sha256Hash(originalQuery) : null,
						replacementQueryHash: null,
						suggestedQueryHash: suggestedQuery ? sha256Hash(suggestedQuery) : null,
					});
				}
			}
		}
	}, [queryInfo, fireShownEvent, originalQueryUrlParam]);

	if (showAutoCorrection) {
		if (!originalQueryUrlParam && replacementQuery) {
			return (
				<Box>
					<Box>
						{/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx */}
						<Text size="large" weight="medium">
							Showing results for{' '}
							<Link
								href={generateSearchUrl({
									...queryParams,
									text: replacementQuery,
									originalQuery,
								})}
								xcss={anchorStyles}
								onClick={() =>
									fireClickEvent({
										autoCorrectionType: 'replacement',
										originalQueryType,
										confidenceScore,
										originalQuery,
										replacementQuery,
										suggestedQuery: null,
										originalQueryHash: originalQuery ? sha256Hash(originalQuery) : null,
										replacementQueryHash: replacementQuery ? sha256Hash(replacementQuery) : null,
										suggestedQueryHash: null,
									})
								}
							>
								<Text as="em" size="large" weight="medium" color="color.link">
									{replacementQuery}
								</Text>
							</Link>
						</Text>
					</Box>
					<Box>
						{/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx */}
						<Text>
							Search instead for{' '}
							<Link
								href={generateSearchUrl({
									...queryParams,
									text: originalQuery,
									originalQuery: replacementQuery,
								})}
								xcss={anchorStyles}
								onClick={() =>
									fireClickEvent({
										autoCorrectionType: 'original',
										originalQueryType,
										confidenceScore,
										originalQuery,
										replacementQuery,
										suggestedQuery: null,
										originalQueryHash: originalQuery ? sha256Hash(originalQuery) : null,
										replacementQueryHash: replacementQuery ? sha256Hash(replacementQuery) : null,
										suggestedQueryHash: null,
									})
								}
							>
								<Text as="em" color="color.link">
									{originalQuery}
								</Text>
							</Link>
						</Text>
					</Box>
				</Box>
			);
		}

		if (!replacementQuery && suggestedQuery) {
			return (
				<Box>
					{/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx */}
					<Text size="large" weight="medium">
						Did you mean{' '}
						<Link
							// TODO: Why changing the query using query params and not Store actions?
							href={generateSearchUrl({
								...queryParams,
								text: suggestedQuery,
								originalQuery: originalQuery,
							})}
							xcss={anchorStyles}
							onClick={() =>
								fireClickEvent({
									autoCorrectionType: 'suggested',
									originalQueryType,
									confidenceScore,
									originalQuery,
									replacementQuery: null,
									suggestedQuery,
									originalQueryHash: originalQuery ? sha256Hash(originalQuery) : null,
									replacementQueryHash: null,
									suggestedQueryHash: suggestedQuery ? sha256Hash(suggestedQuery) : null,
								})
							}
						>
							<Text as="em" size="large" weight="medium" color="color.link">
								{suggestedQuery}
							</Text>
						</Link>
					</Text>
				</Box>
			);
		}
	}

	return null;
};

const AutoCorrectionIfEligible = ({
	appMode,
	node,
	cloudId,
}: AutoCorrectionProps & {
	appMode: ApplicationModes;
}): React.JSX.Element | null => {
	const { locale } = useIntl();
	const { isAdminHubAIEnabled, isRovoLLMEnabled } = useAppContext();
	if (
		!isEnglishLocale(locale) ||
		!areLLMPoweredFeaturesEnabled(appMode, isAdminHubAIEnabled, isRovoLLMEnabled) ||
		appMode !== ApplicationModes.Rovo
	) {
		return null;
	}
	return (
		<UFOSegment name="search-page-auto-correction">
			<AutoCorrection node={node} cloudId={cloudId} />
		</UFOSegment>
	);
};

export default AutoCorrectionIfEligible;
